











































































































import { Component, Vue } from 'vue-property-decorator'
import { availableReferralStatuses, availableReferralStatusReasons, ReferralStatus, ReferralStatusReason, ReferralType } from '@/api/types'
import { Pipeline } from '@/api/model/pipeline'
import { StatusReason } from '@/api/model/status-reason'
import { MigrationMapAPI, PipelineAPI, StatusReasonAPI } from '@/api/backend-client'
import { MigrationMap } from '@/api/model/migration-map'
import { v4 as uuid } from 'uuid'

@Component
export default class LeadTypeMigrationForm extends Vue {
    loading: boolean = true
    migrationMap: MigrationMap = null

    pipelineMigrationMap: {[type in ReferralType]?: string} = {}
    statusMigrationMap: {[type in ReferralType]?: {[status in ReferralStatus]?: { phase: string, phaseReason: string } }} = {}
    reasonMigrationMap: {[type in ReferralType]?: {[reason in ReferralStatusReason]?: string }} = {}

    pipelines: Pipeline[] = []
    statusReasons: StatusReason[] = []

    async mounted() {
        this.loading = true

        // This needs to be called here to prevent race condition
        await this.$currentCompany.ensureIncomingReferralsAreLoaded()

        this.migrationMap = (await MigrationMapAPI.getAll({ company: this.$currentCompany.id }))[0]

        if (this.migrationMap) {
            this.$set(this, 'pipelineMigrationMap', this.migrationMap.pipelineMapping)
            this.$set(this, 'statusMigrationMap', this.migrationMap.phaseMapping)
            this.$set(this, 'reasonMigrationMap', this.migrationMap.reasonMapping)
        } else {
            for (const leadType of this.leadTypes) {
                this.$set(this.pipelineMigrationMap, leadType, null)
                this.$set(this.statusMigrationMap, leadType, {})
                this.$set(this.reasonMigrationMap, leadType, {})

                for (const leadStatus of this.leadStatuses) {
                    this.$set(this.statusMigrationMap[leadType], leadStatus, { phase: null, phaseReason: null })
                }

                for (const leadReason of this.leadReasons) {
                    this.$set(this.reasonMigrationMap[leadType], leadReason, null)
                }
            }
        }

        this.pipelines = await PipelineAPI.getAll({ company: this.$currentCompany.id })
        this.statusReasons = await StatusReasonAPI.getAll({ company: this.$currentCompany.id })

        this.loading = false
    }

    async save() {
        this.loading = true

        if (this.migrationMap) {
            this.migrationMap.pipelineMapping = this.pipelineMigrationMap
            this.migrationMap.phaseMapping = this.statusMigrationMap
            this.migrationMap.reasonMapping = this.reasonMigrationMap

            await MigrationMapAPI.update(
                this.migrationMap.id,
                this.migrationMap,
            )
        } else {
            this.migrationMap = await MigrationMapAPI.create(new MigrationMap(
                this.$currentCompany.resourceIdentifier,
                uuid(),
                this.pipelineMigrationMap,
                this.statusMigrationMap,
                this.reasonMigrationMap,
                new Date(),
                new Date(),
            ))
        }

        this.loading = false

        this.$emit('on-save')
    }

    phaseOptions(pipelineId: string): Array<{label: string, value: string}> {
        const pipeline = this.pipelines.find(item => item.id === pipelineId)

        if (!pipeline) {
            return []
        }

        return pipeline.phases.map(phase => {
            return {
                label: phase.name,
                value: phase.id,
            }
        })
    }

    typeNotMapped(leadType: string): boolean {
        return !this.pipelineMigrationMap[leadType]
    }

    pipelineInUse(pipelineId): boolean {
        if (!pipelineId) {
            return false
        }

        return Object.values(this.pipelineMigrationMap).filter(mapPipelineId => mapPipelineId === pipelineId).length > 1
    }

    statusNotMapped(leadType: string, leadStatus: string): boolean {
        return !this.statusMigrationMap[leadType][leadStatus]
    }

    isInvalid(leadType: string): boolean {
        if (this.typeNotMapped(leadType)) {
            return true
        }

        const unmappedStatuses = Object.values(this.statusMigrationMap[leadType]).filter((statusMap: { phase: string, phaseReason: string }) => {
            return statusMap.phase === null
        })

        return unmappedStatuses.length > 0
    }

    reasonOptions(pipelineId, leadReason): Array<{label: string, value: string}> {
        return this.statusReasons
            .filter(statusReason => statusReason.pipeline.id === pipelineId)
            .filter(statusReason => statusReason.status === this.statusReasonToStatusMapping[leadReason])
            .map(statusReason => {
                return {
                    label: this.$t('referral.status.' + statusReason.status) + ' - ' + statusReason.name,
                    value: statusReason.id,
                }
            })
            .sort((a, b) => a.label.localeCompare(b.label))
    }

    get pipelineOptions(): Array<{label: string, value: string}> {
        return this.pipelines
            .map(pipeline => {
                return {
                    label: pipeline.name,
                    value: pipeline.id,
                }
            })
            .sort((a, b) => a.label.localeCompare(b.label))
    }

    get leadTypes(): string[] {
        return this.$currentCompany.handledIncomingReferralTypes
    }

    get leadStatuses(): ReferralStatus[] {
        return availableReferralStatuses().filter(status => ![
            ReferralStatus.PROCESSED,
            ReferralStatus.CLOSED,
            ReferralStatus.SUCCESSFUL,
        ].includes(status))
    }

    get leadReasons(): ReferralStatusReason[] {
        return availableReferralStatusReasons().filter(reason => ![
            ReferralStatusReason.OTHER,
        ].includes(reason))
    }
    
    get statusReasonToStatusMapping(): {[type in ReferralStatusReason]?: ReferralStatus} {
        return {
            [ReferralStatusReason.WAITING_INTERNAL]: ReferralStatus.OPEN,
            [ReferralStatusReason.WAITING_EXTERNAL]: ReferralStatus.OPEN,
            [ReferralStatusReason.WAITING_CONTACT]: ReferralStatus.OPEN,
            [ReferralStatusReason.INFORMATION]: ReferralStatus.OPEN,
            [ReferralStatusReason.NEGATIVE_ADVICE]: ReferralStatus.CLOSED,
            [ReferralStatusReason.NO_SUITABLE_TIME]: ReferralStatus.OPEN,
            [ReferralStatusReason.NO_TIME]: ReferralStatus.OPEN,
            [ReferralStatusReason.NOT_INTERESTED]: ReferralStatus.CLOSED,
            [ReferralStatusReason.THINKING]: ReferralStatus.OPEN,
            [ReferralStatusReason.TO_COMPETITOR]: ReferralStatus.CLOSED,
            [ReferralStatusReason.VOICEMAIL]: ReferralStatus.OPEN,
            [ReferralStatusReason.WITHDRAWN]: ReferralStatus.CLOSED,
            [ReferralStatusReason.WRONG_CONTACT]: ReferralStatus.OPEN,
            [ReferralStatusReason.WRONG_CONTACT_DETAILS]: ReferralStatus.OPEN,
            [ReferralStatusReason.REOPENED]: ReferralStatus.OPEN,
            [ReferralStatusReason.UNREACHABLE]: ReferralStatus.CLOSED,
            [ReferralStatusReason.LONG_TERM]: ReferralStatus.OPEN,
            [ReferralStatusReason.CONTACTED_PER_EMAIL]: ReferralStatus.OPEN,
            [ReferralStatusReason.WAITING_NEW_BUILD]: ReferralStatus.OPEN,
            [ReferralStatusReason.PROCESSED_REOPENED]: ReferralStatus.OPEN,
            [ReferralStatusReason.PROCESSED_CLOSED]: ReferralStatus.CLOSED,
            [ReferralStatusReason.AUTO_CLOSED]: ReferralStatus.CLOSED,
        }
    }
}
